import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { useAuth } from '@/contexts/AuthContext';
import { Users, CheckCircle, AlertCircle } from 'lucide-react';

export default function JoinTeamPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteCodeFromUrl = searchParams.get('code') || '';

  const [step, setStep] = useState(1);
  const [inviteCode, setInviteCode] = useState(inviteCodeFromUrl);
  const [isValidating, setIsValidating] = useState(false);
  const [inviteData, setInviteData] = useState<{
    orgName: string;
    role: string;
    invitedBy: string;
  } | null>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  // Auto-validate if invite code in URL
  useEffect(() => {
    if (inviteCodeFromUrl) {
      validateInviteCode(inviteCodeFromUrl);
    }
  }, [inviteCodeFromUrl]);

  const validateInviteCode = async (code: string) => {
    if (!code || code.length < 6) {
      setError('Please enter a valid invitation code');
      return;
    }

    setIsValidating(true);
    setError('');

    try {
      // TODO: API call to validate invite code
      // const response = await apiClient.validateInviteCode(code);
      
      // Mock response
      await new Promise(resolve => setTimeout(resolve, 1000));
      setInviteData({
        orgName: 'Acme Corporation',
        role: 'Recruiter',
        invitedBy: 'Jane Smith',
      });
      setStep(2);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid or expired invitation code');
      setInviteData(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 12) {
      setError('Password must be at least 12 characters long');
      setIsLoading(false);
      return;
    }

    try {
      // TODO: Signup with invite code
      await signup({
        ...formData,
        role: 'recruiter', // Role from invitation
        accountType: 'organization',
        inviteCode: inviteCode,
      });

      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}&type=team`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center items-center space-x-2">
          <Users className="h-10 w-10 text-primary-600" />
          <span className="text-2xl font-bold text-gray-900">ePeople Agent</span>
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          Join Your Team
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Enter your invitation code to get started
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border border-gray-200">
          {/* Step 1: Validate Invite Code */}
          {step === 1 && (
            <div className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <Input
                label="Invitation Code"
                required
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="XXXXXX"
                helperText="Enter the 6-character code from your invitation email"
              />

              <Button
                onClick={() => validateInviteCode(inviteCode)}
                isLoading={isValidating}
                className="w-full"
                disabled={inviteCode.length < 6}
              >
                Validate Code
              </Button>

              <div className="mt-6 text-center text-sm text-gray-600">
                <p>Don't have an invitation code?</p>
                <Link to="/get-started" className="font-medium text-primary-600 hover:text-primary-500">
                  Create your own account
                </Link>
              </div>
            </div>
          )}

          {/* Step 2: Invitation Details & Signup */}
          {step === 2 && inviteData && (
            <div className="space-y-6">
              {/* Invitation Info */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-green-900">Valid Invitation</h3>
                    <div className="mt-2 space-y-1 text-sm text-green-800">
                      <p><strong>Organization:</strong> {inviteData.orgName}</p>
                      <p><strong>Role:</strong> {inviteData.role}</p>
                      <p><strong>Invited by:</strong> {inviteData.invitedBy}</p>
                    </div>
                  </div>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Signup Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Create Your Account</h3>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    placeholder="John"
                  />

                  <Input
                    label="Last Name"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    placeholder="Doe"
                  />
                </div>

                <Input
                  label="Email Address"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@company.com"
                  helperText="Use your work email address"
                />

                <Input
                  label="Password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  helperText="At least 12 characters with uppercase, lowercase, number, and symbol"
                />

                <Input
                  label="Confirm Password"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                />

                <div className="flex space-x-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setStep(1);
                      setInviteData(null);
                    }}
                    className="w-full"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isLoading}
                    className="w-full"
                  >
                    Create Account
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
